/*
 * ISG simple order form (19 Oct 2026): the browser side of sections/isg-order-form.liquid.
 * Needs assets/isg-order-core.js (pure logic: totals, checks, basket plan, basket verification).
 *
 * Flow: 1 choose items (colour cards with a quantity per size, or product options and packs), 2 add the design (print
 * locations with artwork or text for clothing; photos/artwork, text and layout when personalisation is included;
 * a numbered photo slot per photo the frame or print holds, with a fit choice, for photo prints and frames),
 * 3 review the price and add to basket.
 *
 * Adding to the basket (reliability, from the 18 Oct review):
 *   - one attempt reference (Design ref) per click; the button is locked while an attempt runs, so no double submits
 *   - each file is uploaded once, with the first basket line that uses it; later lines carry Shopify's stored link
 *   - print charges are added as their own lines, then the whole basket is re-read and checked against the plan
 *   - on any failure the attempt's lines are removed and the basket is re-read; the customer is only told "nothing was
 *     added" when that re-read confirms it. Otherwise the reference is remembered and cleaned up before the next try,
 *     so retrying never leaves duplicates.
 *
 * Colour-first layout (24 Oct 2026): the form can sit in the product column (see the section). Colours show on arrival
 * (a long range shows the first few with "Show all colours"; chosen colours always stay visible), choosing one shows
 * its photo in the product gallery, its sizes open straight away, and "Continue to print" leads on. Anything missing is
 * shown beside its field when the customer tries to add to the basket, with a jump to it that clears the sticky header.
 * On phones a compact running total with the next step stays at the bottom of the screen.
 *
 * Plain (blank) items (24 Oct 2026): on clothing products the print step starts with "Printed" or "Plain – no
 * printing". Plain orders carry no prints, no artwork and no print charge; each basket line says "Printing: None –
 * plain item". The pricing, checks and basket plan are the same shared code, given an order with no prints.
 */
(function (root) {
  'use strict';
  var C = root.ISGOrderCore;
  if (!C) { if (root.console) root.console.error('ISG order form: isg-order-core.js did not load'); return; }
  var doc = root.document;
  var PENDING_KEY = 'isgOrderPending';

  function el(tag, attrs, kids) {
    var n = doc.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v == null || v === false) return;
      if (k === 'class') n.className = v;
      else if (k === 'text') n.textContent = v;
      else if (k.indexOf('on') === 0 && typeof v === 'function') n.addEventListener(k.slice(2), v);
      else n.setAttribute(k, v === true ? '' : v);
    });
    (kids || []).forEach(function (c) { if (c != null) n.appendChild(typeof c === 'string' ? doc.createTextNode(c) : c); });
    return n;
  }

  function sizeText(b) { return b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB'; }

  // Height of whatever stays fixed at the top of the screen (the theme's sticky header, and any other sticky bar in
  // the header group), so jumps land below it instead of under it. The whole header height is used even while it is
  // tucked away, because it slides back in when the customer scrolls up.
  function headerOffset() {
    var h = 0, seen = [];
    var list = doc.querySelectorAll('sticky-header, .shopify-section-header-sticky, .shopify-section-group-header-group');
    Array.prototype.forEach.call(list, function (n) {
      var cs = root.getComputedStyle(n), pos = cs.position;
      var host = n.closest ? n.closest('.shopify-section-group-header-group, .shopify-section') : null;
      if (pos !== 'sticky' && pos !== 'fixed' && n.tagName !== 'STICKY-HEADER') return;
      var box = host || n;
      if (seen.indexOf(box) !== -1) return;
      seen.push(box);
      h += box.getBoundingClientRect().height;
    });
    return Math.round(h) + 16;
  }
  function setOffsetVar() {
    try { doc.documentElement.style.setProperty('--isg-header-offset', headerOffset() + 'px'); } catch (e) { /* ignore */ }
  }
  // Scroll so `target` sits just below the sticky header, then move keyboard focus to `focusEl` (or the target).
  function jumpTo(target, focusEl) {
    if (!target) return;
    var reduce = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var top = target.getBoundingClientRect().top + (root.pageYOffset || doc.documentElement.scrollTop) - headerOffset();
    try { root.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' }); } catch (e) { root.scrollTo(0, Math.max(0, top)); }
    var f = focusEl || target;
    if (f && f.focus) {
      if (!f.hasAttribute('tabindex') && !/^(INPUT|SELECT|TEXTAREA|BUTTON|A)$/.test(f.tagName)) f.setAttribute('tabindex', '-1');
      try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); }
    }
  }

  function Form(box) {
    var id = box.getAttribute('data-isg-order');
    var data = this.data = JSON.parse(doc.getElementById('IsgOrderData-' + id).textContent);
    this.box = box;
    this.q = function (s) { return box.querySelector(s); };
    this.flow = data.flow;
    this.cur = data.currency;
    this.s = data.settings || {};
    this.tiers = C.parseTiers(this.s.bulkTiers);
    this.uid = 1;
    this.files = {};
    this.same = true;
    this.alloc = {};
    this.busy = false;
    this.showErrors = false;   // field messages appear once the customer tries to go ahead
    this.showAllColours = false;
    this.plain = false;        // clothing: plain (blank) items, no printing
    this.designs = [];
    if (this.flow === 'clothing') {
      this.sizesByKey = {};
      var self = this;
      (data.printSizes || []).forEach(function (s) { self.sizesByKey[s.key] = s; });
      this.locByKey = {};
      (data.locations || []).forEach(function (l) {
        l.allowed = String(l.sizes || '').split(',').map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
        l.named = data.printSizes.filter(function (s) { return l.allowed.indexOf(String(s.label).trim().toLowerCase()) !== -1 && s.variantId; });
        // print area: from the template (cm), or from the product's isg.print_area field ("30 x 15", or per size
        // "S: 18 x 22, M: 25 x 28"); only print sizes that fit it are offered
        l.areaBySize = null; l.area = null;
        var txt = String(l.areaText || '');
        if (txt.indexOf(':') !== -1) {
          l.areaBySize = {};
          txt.split(',').forEach(function (part) {
            var m = part.split(':'), a = C.parseArea(m[1]);
            if (m.length === 2 && a) l.areaBySize[m[0].trim().toLowerCase()] = a;
          });
          if (!Object.keys(l.areaBySize).length) l.areaBySize = null;
        } else if (C.parseArea(txt)) l.area = C.parseArea(txt);
        if (!l.area && !l.areaBySize && l.areaW > 0 && l.areaH > 0) l.area = [l.areaW, l.areaH];
        self.locByKey[l.key] = l;
      });
      this.rows = [];          // [{ colour, qty: { size: n } }]
      this.sizeCache = {};     // colour -> sizeValues
      this.loading = {};
      var sel = data.colours.filter(function (c) { return c.selected; })[0];
      if (sel) this.sizeCache[sel.name] = data.sizeValues;
      if (!data.colours.length) { this.noColour = true; this.sizeCache[''] = data.sizeValues; this.rows.push({ colour: '', qty: {} }); }
    } else {
      this.layouts = String(data.layoutsText || '').split(/\r?\n/).map(function (x) { return x.trim(); }).filter(Boolean);
      this.packIndex = data.packIndex;
      this.persIndex = data.persIndex == null ? -1 : data.persIndex;
      this.incRows = [];
      var me = this;
      var avail = data.variants.filter(function (v) { return v.available; });
      // photo flow: start on a "with your photo" choice, not "Frame only"
      var first = (this.flow === 'photo' ? avail.filter(function (v) { return me.photoInfo(v).printed; })[0] : null) || avail[0] || data.variants[0];
      if (first) this.incRows.push(this.newIncRow(first.options.slice()));
    }
    if (this.flow !== 'photo') this.addDesign();
    this.render();
    this.setupSticky();
  }

  var P = Form.prototype;

  // Print sizes offered at a location: those named for it, and only those that fit its print area. Where the area
  // depends on the product size, the smallest area among the sizes in the order decides (all sizes until some are chosen).
  P.locArea = function (l) {
    if (!l.areaBySize) return l.area;
    var self = this, chosen = [];
    (this.rows || []).forEach(function (r) { Object.keys(r.qty).forEach(function (sz) { if (r.qty[sz] && chosen.indexOf(sz) === -1) chosen.push(sz); }); });
    var areas = (chosen.length ? chosen : Object.keys(l.areaBySize)).map(function (sz) { return l.areaBySize[String(sz).trim().toLowerCase()]; }).filter(Boolean);
    if (!areas.length) return null;
    var pick = areas[0];
    areas.forEach(function (a) { if (chosen.length ? a[0] * a[1] < pick[0] * pick[1] : a[0] * a[1] > pick[0] * pick[1]) pick = a; });
    return pick;
  };
  P.locSizes = function (l) {
    var a = this.locArea(l);
    var fit = C.sizesThatFit(l.named, a ? a[0] : 0, a ? a[1] : 0);
    return l.named.filter(function (s) { return fit.indexOf(s.key) !== -1; }).map(function (s) { return s.key; });
  };

  /* ---------- helpers ---------- */
  P.money = function (p) { return C.money(p, this.cur); };
  P.announce = function (msg) { var l = this.q('[data-o-live]'); l.textContent = ''; setTimeout(function () { l.textContent = msg; }, 40); };
  P.many = function () { return !this.same && this.designs.length > 1; };

  P.variantFor = function (opts) {
    return this.data.variants.filter(function (v) { return v.options.every(function (o, i) { return o === opts[i]; }); })[0] || null;
  };
  P.optLabel = function (v) {
    var pi = this.packIndex;
    var parts = v.options.filter(function (o, i) { return i !== pi; });
    return parts.join(' / ');
  };
  P.pack = function (v) { return this.packIndex >= 0 ? C.packSize(v.options[this.packIndex]) : 1; };
  P.newIncRow = function (opts) { return { uid: this.uid++, opts: opts, packs: 1, same: true, sets: [] }; };
  P.photoInfo = function (v) { return C.photoInfo(v.options, this.persIndex, this.packIndex, this.data.productTitle, this.data.photoCountOverride); };
  P.noun = function (v) { return this.pack(v) > 1 ? 'pack' : this.persIndex >= 0 ? 'frame' : 'print'; };
  P.capFor = function (v) {
    var m = v.stock != null ? Math.max(0, v.stock) : null;
    if (v.qtyMax && (m == null || v.qtyMax < m)) m = v.qtyMax;
    return m;
  };

  // Order lines for the core: one per variant.
  P.lines = function () {
    var self = this, out = [];
    if (this.flow === 'clothing') {
      this.rows.forEach(function (r) {
        (self.sizeCache[r.colour] || []).forEach(function (sv) {
          var q = r.qty[sv.name] || 0, v = sv.variant;
          if (!q || !v) return;
          out.push({ key: String(v.id), variantId: v.id, items: q, units: q, price: v.price, eligible: !!self.data.bulkEligible,
                     label: (r.colour ? r.colour + ' ' : '') + sv.name, colour: r.colour, size: sv.name, stock: self.capFor(v) });
        });
      });
    } else {
      this.incRows.forEach(function (r) {
        var v = self.variantFor(r.opts);
        if (!v || !r.packs) return;
        var pk = self.pack(v), name = self.optLabel(v);
        var noun = self.flow === 'photo' ? self.noun(v) : 'item';
        var line = { key: String(v.id), variantId: v.id, items: pk * r.packs, units: r.packs, price: v.price, eligible: !!self.data.bulkEligible,
                   label: (name ? name + ', ' : '') + (pk > 1 ? r.packs + ' × pack of ' + pk : r.packs + ' ' + noun + (r.packs === 1 ? '' : 's')), stock: self.capFor(v), rowUid: r.uid };
        if (self.flow === 'photo') {
          var info = self.photoInfo(v);
          line.printed = info.printed; line.count = info.count; line.sizeIn = info.sizeIn; line.noun = noun; line.row = r; line.name = name;
        }
        out.push(line);
      });
    }
    return out;
  };

  P.coreDesigns = function () {
    var self = this;
    return this.designs.map(function (d) {
      if (self.flow !== 'clothing') return d;
      return { id: d.id, prints: d.prints.map(function (p) {
        var loc = self.locByKey[p.location];
        return { location: p.location, locationLabel: loc ? loc.name + (loc.note ? ' (' + loc.note + ')' : '') : '', sizeKey: p.sizeKey,
                 allowed: loc ? self.locSizes(loc) : [], fileIds: p.fileIds, text: p.text, perItem: p.perItem, textEach: p.textEach, notes: p.notes };
      }) };
    });
  };

  P.state = function () {
    var lines = this.lines();
    var plain = this.isPlain();
    var st = { flow: this.flow, lines: lines, designs: plain ? [{ id: 'A', prints: [] }] : this.coreDesigns(), same: plain || this.same || this.designs.length < 2, alloc: plain ? {} : this.alloc,
             files: this.files, sizes: this.sizesByKey, currency: this.cur, productTitle: this.data.productTitle, plain: plain,
             settings: { requirePrint: plain ? false : this.s.requirePrint, allowTextOnly: this.s.allowTextOnly, layouts: this.layouts, maxPhotoSets: this.s.maxPhotoSets } };
    if (this.flow === 'photo') {
      this.syncPhotoRows(lines);
      st.photoRows = lines.map(function (l) {
        return { key: l.key, label: l.label, variantId: l.variantId, units: l.units, items: l.items, printed: l.printed, count: l.count,
                 sizeIn: l.sizeIn, noun: l.noun, same: l.row.same || l.units < 2,
                 sets: l.row.sets.map(function (x) { return { slots: x.slots.slice(), fit: x.fit, notes: x.notes }; }) };
      });
    }
    return st;
  };

  // Photo flow: one set of photo slots per row (same photos on every frame or pack), or one per frame or pack.
  P.syncPhotoRows = function (lines) {
    var self = this, max = this.s.maxPhotoSets || 20;
    (lines || this.lines()).forEach(function (l) {
      var r = l.row;
      if (l.units < 2) r.same = true;
      var need = !l.printed ? 0 : r.same ? 1 : Math.min(l.units, max);
      while (r.sets.length < need) r.sets.push({ uid: self.uid++, slots: [], fit: 'fill', notes: '' });
      if (r.sets.length > need) r.sets.length = need;
      r.sets.forEach(function (x) {
        while (x.slots.length < l.count) x.slots.push(null);
        if (x.slots.length > l.count) x.slots.length = l.count;
      });
    });
  };

  P.isPlain = function () { return this.flow === 'clothing' && !!this.s.allowPlain && this.plain; };

  /* ---------- designs ---------- */
  P.addDesign = function () {
    var used = this.designs.map(function (d) { return d.id; });
    var id = C.LETTERS.split('').filter(function (l) { return used.indexOf(l) === -1; })[0];
    var d = this.flow === 'clothing' ? { id: id, prints: [] } : { id: id, fileIds: [], text: '', perItem: false, textEach: {}, layout: this.layouts.length === 1 ? this.layouts[0] : '', notes: '' };
    if (this.flow === 'clothing') d.prints.push(this.newPrint());
    this.designs.push(d);
    return d;
  };
  P.newPrint = function () {
    return { uid: this.uid++, location: '', sizeKey: '', fileIds: [], text: '', perItem: false, textEach: {}, notes: '' };
  };
  P.removeDesign = function (id) {
    var self = this;
    if (this.designs.length < 2) return;
    this.designs = this.designs.filter(function (d) { return d.id !== id; });
    Object.keys(this.alloc).forEach(function (k) { delete self.alloc[k][id]; });
    if (this.designs.length < 2) this.same = true;
    this.dropUnusedFiles();
    this.render();
    this.announce('Design ' + id + ' removed.');
  };

  /* ---------- files ---------- */
  P.fileUses = function (fid) {
    var n = 0;
    this.designs.forEach(function (d) {
      if (d.prints) d.prints.forEach(function (p) { if (p.fileIds.indexOf(fid) !== -1) n++; });
      else if (d.fileIds.indexOf(fid) !== -1) n++;
    });
    (this.incRows || []).forEach(function (r) { (r.sets || []).forEach(function (x) { if (x.slots.indexOf(fid) !== -1) n++; }); });
    return n;
  };
  P.dropUnusedFiles = function () {
    var self = this;
    Object.keys(this.files).forEach(function (fid) {
      if (!self.fileUses(fid)) { var f = self.files[fid]; if (f.thumb) URL.revokeObjectURL(f.thumb); delete self.files[fid]; }
    });
  };
  // Add chosen files to a target (a print or a design): each is checked from its own bytes; nothing is altered.
  P.addFiles = function (target, list, replaceId) {
    var self = this, max = this.s.maxFiles || 5;
    var arr = Array.prototype.slice.call(list || []);
    if (!replaceId && target.fileIds.length + arr.length > max) {
      this.toast(target, 'You can add up to ' + max + ' files here. Remove one first, or email extra files to us after ordering.');
      arr = arr.slice(0, Math.max(0, max - target.fileIds.length));
    }
    arr.forEach(function (file) {
      var rec = self.newFile(file);
      if (replaceId) {
        var i = target.fileIds.indexOf(replaceId);
        if (i !== -1) target.fileIds.splice(i, 1, rec.id); else target.fileIds.push(rec.id);
        replaceId = null;
      } else target.fileIds.push(rec.id);
    });
    this.dropUnusedFiles();
    this.renderDesign();
    this.update();
  };
  // A new file record, checked from its own bytes in the background (status 'checking' until done). Nothing is altered:
  // the thumbnail and pixel size come from the browser reading the file, and the original is what gets uploaded.
  P.newFile = function (file) {
    var self = this, fid = 'f' + this.uid++;
    var rec = { id: fid, file: file, name: file.name || 'file', size: file.size, status: 'checking', message: '', thumb: null, url: null, px: null };
    this.files[fid] = rec;
    file.slice(0, 16).arrayBuffer().then(function (buf) {
      var res = C.checkFile(file, new Uint8Array(buf), self.s.maxFileMb || 20);
      rec.message = res.ok ? '' : res.message;
      rec.type = res.type;
      if (!res.ok) { rec.status = 'error'; return null; }
      if (res.type === 'application/pdf') { rec.status = 'ready'; return null; }
      rec.thumb = URL.createObjectURL(file);
      return new Promise(function (done) {
        var img = new Image();
        img.onload = function () { rec.px = [img.naturalWidth, img.naturalHeight]; done(); };
        img.onerror = function () { done(); };
        img.src = rec.thumb;
      }).then(function () { rec.status = 'ready'; });
    }).catch(function () {
      rec.status = 'error';
      rec.message = 'We couldn’t read this file on your device. Please try another copy of it.';
    }).then(function () {
      self.dropUnusedFiles();
      self.renderDesign();
      self.update();
      self.announce(rec.status === 'ready' ? rec.name + ' added.' : rec.name + ': ' + rec.message);
    });
    return rec;
  };
  P.removeFile = function (target, fid) {
    target.fileIds = target.fileIds.filter(function (x) { return x !== fid; });
    var name = this.files[fid] ? this.files[fid].name : 'File';
    this.dropUnusedFiles();
    this.renderDesign();
    this.update();
    this.announce(name + ' removed.');
  };
  P.toast = function (target, msg) { target._msg = msg; };

  /* ---------- step 1: items ---------- */
  P.render = function () { this.renderItems(); this.renderDesign(); this.update(); };

  P.renderItems = function () {
    var wrap = this.q('[data-o-items]');
    var active = doc.activeElement && wrap.contains(doc.activeElement) ? doc.activeElement.id : null;
    wrap.innerHTML = '';
    if (this.flow === 'clothing') this.renderClothingItems(wrap); else this.renderIncludedItems(wrap);
    if (active) { var a = doc.getElementById(active); if (a) a.focus(); }
  };

  P.renderClothingItems = function (wrap) {
    var self = this, d = this.data;
    if (!this.noColour) {
      var total = d.colours.length, limit = Math.max(1, C.toInt(this.s.colourLimit) || 12);
      var compact = !this.showAllColours && total > limit + 4;
      var legend = 'Choose a colour' + (this.rows.length ? ' (add as many as you like)' : '') + (total > 1 ? ' – ' + total + ' colours' : '');
      var fs = el('fieldset', { class: 'isg-order__fieldset' }, [el('legend', { class: 'isg-order__label', text: legend })]);
      var sw = el('div', { class: 'isg-order__swatches', id: 'IsgSwatches-' + d.sectionId });
      var hiddenCount = 0;
      d.colours.forEach(function (c, ci) {
        var on = !!self.row(c.name);
        if (compact && ci >= limit && !on) { hiddenCount++; return; }
        var b = el('button', { type: 'button', class: 'isg-order__swatch' + (on ? ' is-on' : ''), 'aria-pressed': on ? 'true' : 'false',
          'data-colour': c.name,
          onclick: function () { if (self.row(c.name)) { self.showColourPhoto(c.name); self.focusRow(c.name); } else self.addRow(c.name); } }, [
          c.thumb ? el('img', { src: c.thumb, alt: '', width: '48', height: '48', loading: 'lazy' }) : el('span', { class: 'isg-order__chip', style: c.swatch ? 'background:' + c.swatch : null }),
          el('span', { class: 'isg-order__swatch-name', text: c.name }),
          on ? el('span', { class: 'isg-order__swatch-tick', 'aria-hidden': 'true', text: '✓' }) : null
        ]);
        sw.appendChild(b);
      });
      fs.appendChild(sw);
      if (total > limit + 4) {
        fs.appendChild(el('button', { type: 'button', class: 'isg-order__link isg-order__more', 'aria-expanded': compact ? 'false' : 'true', 'aria-controls': 'IsgSwatches-' + d.sectionId,
          onclick: function () {
            self.showAllColours = !self.showAllColours; self.renderItems();
            var m = self.q('.isg-order__more'); if (m) m.focus();
          } }, [compact ? 'Show all ' + total + ' colours (' + hiddenCount + ' more)' : 'Show fewer colours']));
      }
      wrap.appendChild(fs);
    }
    wrap.appendChild(el('p', { class: 'isg-order__field-error', 'data-o-err': 'items', hidden: true }));
    if (!this.rows.length) wrap.appendChild(el('p', { class: 'isg-order__hint', text: this.noColour ? 'Enter how many you need.' : 'Tap a colour to add it. Its sizes open straight away – enter how many you need in each size.' }));
    this.rows.forEach(function (r) { wrap.appendChild(self.colourCard(r)); });
    if (this.rows.length) {
      wrap.appendChild(el('button', { type: 'button', class: 'button button--secondary isg-order__continue', 'data-o-continue': '', hidden: true,
        onclick: function () { self.goToDesign(); } }, ['Continue to print']));
    }
  };

  // Show the chosen colour's own photo in the product gallery (the store's variant image for that colour). Only the
  // gallery's own slides change: the page doesn't scroll away from the form.
  P.showColourPhoto = function (colour) {
    var info = (this.data.colours || []).filter(function (c) { return c.name === colour; })[0];
    if (!info || !info.media) return;
    var self = this;
    var target = null;
    Array.prototype.forEach.call(doc.querySelectorAll('[data-media-id$="-' + info.media + '"]'), function (n) { if (!target && !self.box.contains(n)) target = n; });
    if (!target) return;
    var gallery = target.closest('media-gallery');
    var viewer = gallery && gallery.elements && gallery.elements.viewer || target.parentElement;
    var mid = target.getAttribute('data-media-id');
    try {
      Array.prototype.forEach.call(viewer.querySelectorAll('[data-media-id]'), function (n) { n.classList.remove('is-active'); });
      target.classList.add('is-active');
      var hiddenNow = root.getComputedStyle(target).display === 'none';
      var thumbs = gallery && gallery.elements && gallery.elements.thumbnails;
      var thumb = thumbs ? thumbs.querySelector('[data-target="' + mid + '"]') : null;
      if (hiddenNow) {
        // variant photos hidden by the theme's "hide other variants' media": bring this one to the front
        if (target.parentElement.firstElementChild !== target) target.parentElement.prepend(target);
        if (thumb && thumb.parentElement.firstElementChild !== thumb) thumb.parentElement.prepend(thumb);
        if (viewer.resetPages) viewer.resetPages();
      }
      if (target.parentElement && target.parentElement.scrollTo) target.parentElement.scrollTo({ left: target.offsetLeft });
      if (thumb && gallery.setActiveThumbnail) gallery.setActiveThumbnail(thumb);
      this.announce('Showing ' + colour + '.');
    } catch (e) { /* the gallery is the theme's; never let it stop the form */ }
  };

  // The size guide lives in the product description section underneath; open it and bring it into view.
  P.sizeGuide = function () {
    var det = doc.querySelector('details[id^="Details-isg-info-desc-"]') || doc.querySelector('details[id^="Details-isg-info-c1-"]');
    if (!det) return null;
    return det;
  };
  P.openSizeGuide = function () {
    var det = this.sizeGuide();
    if (!det) return;
    det.open = true;
    jumpTo(det, det.querySelector('summary'));
  };

  // "Continue to print": to the first print that still needs something (or the print step).
  P.goToDesign = function () {
    var step = this.q('[aria-labelledby^="IsgStep2-"]');
    var focus = null;
    if (this.flow === 'clothing') {
      var cards = this.box.querySelectorAll('.isg-order__print');
      for (var i = 0; i < cards.length && !focus; i++) {
        var selEl = cards[i].querySelector('select');
        if (selEl && !selEl.value) focus = selEl;
      }
      if (!focus) focus = this.box.querySelector('.isg-order__print select, [data-o-design] .isg-order__add');
    }
    jumpTo(step, focus || this.q('#IsgStep2-' + this.data.sectionId));
  };

  P.row = function (colour) { return this.rows.filter(function (r) { return r.colour === colour; })[0] || null; };
  P.focusRow = function (colour) {
    var card = this.box.querySelector('.isg-order__card[data-colour="' + (root.CSS && CSS.escape ? CSS.escape(colour) : colour) + '"] input:not([disabled])');
    if (card) card.focus();
  };

  P.addRow = function (colour) {
    var self = this;
    this.rows.push({ colour: colour, qty: {} });
    this.showColourPhoto(colour);
    if (!this.sizeCache[colour]) {
      this.loading[colour] = true;
      this.fetchSizes(colour).then(function (list) { self.sizeCache[colour] = list; }).catch(function () {
        self.loadError = 'We couldn’t load the sizes for ' + colour + '. Check your connection, then remove the colour and add it again.';
      }).then(function () { delete self.loading[colour]; self.renderItems(); self.update(); self.focusRow(colour); });
    }
    this.renderItems();
    this.update();
    this.announce(colour + ' added. Enter quantities for each size.');
    if (!this.loading[colour]) this.focusRow(colour);
  };
  P.removeRow = function (colour) {
    this.rows = this.rows.filter(function (r) { return r.colour !== colour; });
    this.renderItems();
    this.renderDesign();
    this.update();
    this.announce(colour + ' removed.');
  };

  // Another colour's sizes, prices and stock, from this section rendered for that colour.
  P.fetchSizes = function (colour) {
    var d = this.data, info = d.colours.filter(function (c) { return c.name === colour; })[0];
    var url = d.productUrl + (d.productUrl.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(d.sectionId) +
      '&option_values=' + encodeURIComponent(info.id) + (d.view ? '&view=' + encodeURIComponent(d.view) : '');
    return fetch(url, { headers: { Accept: 'text/html' } }).then(function (r) {
      if (!r.ok) throw new Error('sizes');
      return r.text();
    }).then(function (html) {
      var node = new DOMParser().parseFromString(html, 'text/html').getElementById('IsgOrderData-' + d.sectionId);
      if (!node) throw new Error('sizes');
      var j = JSON.parse(node.textContent);
      var sel = j.colours.filter(function (c) { return c.selected; })[0];
      if (!sel || sel.name !== colour) throw new Error('sizes');
      return j.sizeValues;
    });
  };

  P.colourCard = function (r) {
    var self = this, d = this.data;
    var info = d.colours.filter(function (c) { return c.name === r.colour; })[0] || {};
    var card = el('div', { class: 'isg-order__card', 'data-colour': r.colour });
    if (!this.noColour) {
      card.appendChild(el('div', { class: 'isg-order__card-head' }, [
        info.thumb ? el('img', { src: info.thumb, alt: '', width: '40', height: '40', class: 'isg-order__card-thumb' }) : null,
        el('h3', { class: 'isg-order__h3', text: r.colour }),
        el('button', { type: 'button', class: 'isg-order__link', 'aria-label': 'Remove ' + r.colour, onclick: function () { self.removeRow(r.colour); } }, ['Remove'])
      ]));
    }
    if (this.sizeGuide() && (this.sizeCache[r.colour] || []).length > 1) {
      card.appendChild(el('p', { class: 'isg-order__sizeguide' }, [
        el('span', { text: 'How many in each size?' }),
        el('button', { type: 'button', class: 'isg-order__link', onclick: function () { self.openSizeGuide(); } }, ['Size guide'])
      ]));
    }
    if (this.loading[r.colour]) { card.appendChild(el('p', { class: 'isg-order__hint', text: 'Loading sizes…' })); return card; }
    if (!this.sizeCache[r.colour]) { card.appendChild(el('p', { class: 'isg-order__error', text: this.loadError || 'Sizes unavailable.' })); return card; }
    var list = this.sizeCache[r.colour], base = null;
    list.forEach(function (sv) { if (sv.variant && base == null) base = sv.variant.price; });
    var grid = el('div', { class: 'isg-order__sizes', role: 'group', 'aria-label': 'Quantities for ' + (r.colour || d.productTitle) });
    list.forEach(function (sv, i) {
      var v = sv.variant, inputId = 'IsgQ-' + d.sectionId + '-' + self.rows.indexOf(r) + '-' + i;
      var cap = v ? self.capFor(v) : 0;
      var off = !v || !v.available || cap === 0;
      var note = !v ? 'Not made' : !v.available || cap === 0 ? 'Sold out' : (v.price !== base ? self.money(v.price) : '');
      if (v && !off && v.stock != null && v.stock < 10) note = (note ? note + ' · ' : '') + v.stock + ' left';
      var input = el('input', { type: 'number', id: inputId, min: '0', step: '1', inputmode: 'numeric', class: 'isg-order__qty', placeholder: '0',
        value: r.qty[sv.name] ? String(r.qty[sv.name]) : '', disabled: off, max: cap != null ? String(cap) : null, 'aria-describedby': inputId + '-n' });
      input.addEventListener('input', function () {
        var n = C.toInt(input.value);
        if (cap != null && n > cap) { n = cap; input.value = String(cap); self.announce('Only ' + cap + ' of ' + r.colour + ' ' + sv.name + ' available.'); }
        if (n) r.qty[sv.name] = n; else delete r.qty[sv.name];
        self.update();
      });
      input.addEventListener('change', function () { self.refreshIfShapeChanged(); });
      grid.appendChild(el('div', { class: 'isg-order__size' + (off ? ' is-off' : '') }, [
        el('label', { for: inputId, class: 'isg-order__size-name', text: sv.name }), input,
        el('span', { class: 'isg-order__size-note', id: inputId + '-n', text: note })
      ]));
    });
    card.appendChild(grid);
    card.appendChild(el('p', { class: 'isg-order__card-total', 'data-o-rowtotal': r.colour }));
    return card;
  };

  P.renderIncludedItems = function (wrap) {
    var self = this, d = this.data;
    if (d.variantsTruncated) wrap.appendChild(el('p', { class: 'isg-order__error', text: 'Some options of this product can’t be shown here. Please contact us to order it.' }));
    this.incRows.forEach(function (r, ri) {
      var card = el('div', { class: 'isg-order__card' });
      var v = self.variantFor(r.opts);
      card.appendChild(el('div', { class: 'isg-order__card-head' }, [
        el('h3', { class: 'isg-order__h3', text: self.incRows.length > 1 ? 'Choice ' + (ri + 1) : 'Your choice' }),
        self.incRows.length > 1 ? el('button', { type: 'button', class: 'isg-order__link', onclick: function () {
          self.incRows = self.incRows.filter(function (x) { return x !== r; }); self.renderItems(); self.renderDesign(); self.update();
        } }, ['Remove']) : null
      ]));
      var fields = el('div', { class: 'isg-order__opts' });
      d.options.forEach(function (o, oi) {
        if (self.flow === 'photo' && oi === self.persIndex) { fields.appendChild(self.persChoice(r, o, oi)); return; }
        if (o.values.length < 2 && oi !== self.packIndex) return; // nothing to choose
        var sid = 'IsgOpt-' + d.sectionId + '-' + r.uid + '-' + oi;
        var sel = el('select', { id: sid, class: 'isg-order__select' });
        o.values.forEach(function (val) {
          var test = r.opts.slice(); test[oi] = val;
          var tv = self.variantFor(test);
          var txt = val;
          if (oi === self.packIndex && tv) {
            var pk = self.pack(tv);
            txt = (pk > 1 ? 'Pack of ' + pk : val + (pk === 1 ? ' item' : '')) + ' – ' + self.money(tv.price) + (pk > 1 ? ' (' + self.money(Math.round(tv.price / pk)) + ' each)' : '');
          }
          if (!tv) txt += ' – not available';
          else if (!tv.available) txt += ' – sold out';
          sel.appendChild(el('option', { value: val, selected: r.opts[oi] === val, disabled: !tv || !tv.available }, [txt]));
        });
        sel.addEventListener('change', function () { r.opts[oi] = sel.value; self.renderItems(); self.renderDesign(); self.update(); });
        fields.appendChild(el('div', { class: 'isg-order__field' }, [el('label', { for: sid, class: 'isg-order__label', text: oi === self.packIndex ? 'Pack size' : o.name }), sel]));
      });
      var qid = 'IsgPacks-' + d.sectionId + '-' + r.uid;
      var cap = v ? self.capFor(v) : 0;
      var qin = el('input', { type: 'number', id: qid, min: '1', step: '1', inputmode: 'numeric', class: 'isg-order__qty', value: String(r.packs || ''), max: cap != null ? String(cap) : null, disabled: !v || !v.available });
      qin.addEventListener('input', function () {
        var n = C.toInt(qin.value);
        if (cap != null && n > cap) { n = cap; qin.value = String(cap); }
        r.packs = n; self.update();
      });
      qin.addEventListener('change', function () { self.refreshIfShapeChanged(); });
      fields.appendChild(el('div', { class: 'isg-order__field' }, [el('label', { for: qid, class: 'isg-order__label', text: self.packIndex >= 0 && v && self.pack(v) > 1 ? 'Number of packs' : 'Quantity' }), qin]));
      card.appendChild(fields);
      if (!v) card.appendChild(el('p', { class: 'isg-order__error', text: 'That combination isn’t available. Please choose another option.' }));
      else if (!v.available) card.appendChild(el('p', { class: 'isg-order__error', text: 'Sold out. Please choose another option.' }));
      card.appendChild(el('p', { class: 'isg-order__card-total', 'data-o-incrow': String(r.uid) }));
      wrap.appendChild(card);
    });
    if (d.variants.length > 1) {
      wrap.appendChild(el('button', { type: 'button', class: 'button button--secondary isg-order__add', onclick: function () {
        var last = self.incRows[self.incRows.length - 1];
        self.incRows.push(self.newIncRow(last ? last.opts.slice() : d.variants[0].options.slice()));
        self.renderItems(); self.renderDesign(); self.update();
      } }, [self.flow === 'photo' ? '+ Add another choice' : '+ Add another option']));
    }
  };

  // Photo flow: "With your photo printed" or "Frame only" as two clear choices, each with its price.
  P.persChoice = function (r, o, oi) {
    var self = this, name = 'IsgPers-' + this.data.sectionId + '-' + r.uid;
    var fs = el('fieldset', { class: 'isg-order__fieldset isg-order__pers' }, [el('legend', { class: 'isg-order__label', text: o.name })]);
    o.values.forEach(function (val, vi) {
      var test = r.opts.slice(); test[oi] = val;
      var tv = self.variantFor(test), info = tv ? self.photoInfo(tv) : null;
      var rid = name + '-' + vi;
      var inp = el('input', { type: 'radio', name: name, id: rid, value: val, checked: r.opts[oi] === val, disabled: !tv || !tv.available });
      inp.addEventListener('change', function () { r.opts[oi] = val; self.renderItems(); self.renderDesign(); self.update(); });
      var sub = !tv ? 'Not available with the options above' : !tv.available ? 'Sold out'
        : info.printed ? (info.count > 1 ? 'Send us ' + info.count + ' photos and we print and fit them for you' : 'Send us your photo and we print and fit it for you')
        : 'Just the empty frame – no photo needed';
      fs.appendChild(el('label', { for: rid, class: 'isg-order__option' }, [inp, el('span', {}, [
        el('strong', { text: val }), tv ? el('span', { class: 'isg-order__price', text: ' ' + self.money(tv.price) }) : null,
        el('span', { class: 'isg-order__option-sub', text: sub })
      ])]));
    });
    return fs;
  };

  /* ---------- step 2: design ---------- */
  P.renderDesign = function () {
    var self = this, wrap = this.q('[data-o-design]');
    var active = doc.activeElement && wrap.contains(doc.activeElement) ? doc.activeElement.id : null;
    wrap.innerHTML = '';
    if (this.flow === 'photo') {
      this.renderPhotoDesign(wrap);
      if (active) { var pa = doc.getElementById(active); if (pa) pa.focus(); }
      this._lineShape = this.lineShape();
      return;
    }
    var lines = this.lines();
    var totalItems = lines.reduce(function (a, l) { return a + l.items; }, 0);
    if (this.flow === 'clothing' && this.s.allowPlain) {
      var pid = 'IsgPlain-' + this.data.sectionId;
      var pfs = el('fieldset', { class: 'isg-order__fieldset isg-order__printed' }, [el('legend', { class: 'isg-order__label', text: 'Printed or plain?' })]);
      [['printed', 'Printed', 'Add your logo, artwork or text below'], ['plain', 'Plain – no printing', 'Sent blank, exactly as shown. No print charge.']].forEach(function (o) {
        var rid = pid + '-' + o[0];
        var r = el('input', { type: 'radio', name: pid, id: rid, value: o[0], checked: (o[0] === 'plain') === self.plain });
        r.addEventListener('change', function () {
          self.plain = o[0] === 'plain';
          self.renderDesign(); self.update();
          self.announce(self.plain ? 'Plain items: no printing, no print charge.' : 'Printed: add your design.');
          var f = doc.getElementById(rid); if (f) f.focus();
        });
        pfs.appendChild(el('label', { for: rid, class: 'isg-order__option' }, [r, el('span', {}, [el('strong', { text: o[1] }), el('span', { class: 'isg-order__option-sub', text: o[2] })])]));
      });
      wrap.appendChild(pfs);
      this.toggleDesignExtras();
      if (this.plain) {
        wrap.appendChild(el('p', { class: 'isg-order__hint', text: 'Your items are sent plain, with nothing printed on them. Want a print after all? Choose “Printed” above.' }));
        this._lineShape = this.lineShape();
        return;
      }
    }
    // Same design on every item?
    if (totalItems > 1 || !this.same) {
      var qid = 'IsgSame-' + this.data.sectionId;
      var fs = el('fieldset', { class: 'isg-order__fieldset isg-order__same' }, [el('legend', { class: 'isg-order__label', text: 'Is the same design and placement used on every item?' })]);
      [['yes', 'Yes'], ['no', 'No, some items are different']].forEach(function (o) {
        var rid = qid + '-' + o[0];
        var r = el('input', { type: 'radio', name: qid, id: rid, value: o[0], checked: (o[0] === 'yes') === self.same });
        r.addEventListener('change', function () {
          self.same = o[0] === 'yes';
          if (!self.same && self.designs.length < 2) self.addDesign();
          self.renderDesign(); self.update();
        });
        fs.appendChild(el('label', { for: rid, class: 'isg-order__radio' }, [r, ' ' + o[1]]));
      });
      wrap.appendChild(fs);
    }
    wrap.appendChild(el('div', { 'data-o-panels': '' }));
    var many = this.many();
    if (many) {
      wrap.appendChild(el('button', { type: 'button', class: 'button button--secondary isg-order__add', onclick: function () { self.addDesign(); self.renderDesign(); self.update(); } }, ['+ Add another design']));
      wrap.appendChild(this.allocTable(lines));
    }
    this.renderPanels();
    if (active) { var a = doc.getElementById(active); if (a) a.focus(); }
    this._lineShape = this.lineShape();
  };

  // Notes that only apply to printed items are hidden for plain orders.
  P.toggleDesignExtras = function () {
    var plain = this.isPlain();
    Array.prototype.forEach.call(this.box.querySelectorAll('.isg-order__reassure, .isg-order__mockup, .isg-order__help, [data-o-files-note], [aria-labelledby^="IsgStep2-"] > .isg-order__hint:not(.isg-order__help)'), function (n) {
      if (n.closest('[data-o-design]')) return;
      n.hidden = plain;
    });
  };

  // The design panels (prints, or the personalisation card) on their own, so the split table keeps its focus.
  P.renderPanels = function () {
    var self = this, wrap = this.q('[data-o-panels]');
    if (!wrap) return;
    var active = doc.activeElement && wrap.contains(doc.activeElement) ? doc.activeElement.id : null;
    wrap.innerHTML = '';
    var many = this.many();
    this.designs.forEach(function (dz) {
      if (!many && dz !== self.designs[0]) return;
      var panel = el('div', { class: 'isg-order__design' });
      if (many) panel.appendChild(el('div', { class: 'isg-order__card-head' }, [
        el('h3', { class: 'isg-order__h3', text: 'Design ' + dz.id }),
        el('button', { type: 'button', class: 'isg-order__link', onclick: function () { self.removeDesign(dz.id); } }, ['Remove design ' + dz.id])
      ]));
      if (self.flow === 'clothing') {
        dz.prints.forEach(function (p, i) { panel.appendChild(self.printCard(dz, p, i)); });
        panel.appendChild(el('button', { type: 'button', class: 'button button--secondary isg-order__add', onclick: function () {
          var np = self.newPrint(); dz.prints.push(np); self.renderDesign(); self.update();
          var f = self.box.querySelector('#IsgLoc-' + np.uid); if (f) f.focus();
        } }, [dz.prints.length ? '+ Add another print location' : '+ Add a print location']));
      } else {
        panel.appendChild(self.includedCard(dz));
      }
      wrap.appendChild(panel);
    });
    if (active) { var a = doc.getElementById(active); if (a) a.focus(); }
    this._allocShape = this.allocShape();
  };

  // What step 2 shows depends on: the lines ordered (the split table) and how many items each design has on each line
  // (the "one line per item" text boxes). Only the part that depends on a change is redrawn.
  P.lineShape = function () {
    return JSON.stringify(this.lines().map(function (l) { return [l.key, l.items, l.units, l.count]; }));
  };
  P.allocShape = function () {
    var st = this.state(), al = C.allocation(st.lines, st.designs, st.same, st.alloc);
    return JSON.stringify(st.lines.map(function (l) { return [l.key, al[l.key]]; }));
  };
  P.refreshIfShapeChanged = function () {
    if (this.lineShape() !== this._lineShape) this.renderDesign();
    else if (this.flow !== 'photo' && this.allocShape() !== this._allocShape) this.renderPanels();
  };

  P.fileBlock = function (target, idBase, label) {
    var self = this;
    var box = el('div', { class: 'isg-order__files' });
    var fin = el('input', { type: 'file', id: idBase + '-file', multiple: true, accept: '.png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf', class: 'visually-hidden' });
    fin.addEventListener('change', function () { self.addFiles(target, fin.files); fin.value = ''; });
    box.appendChild(fin);
    box.appendChild(el('label', { for: idBase + '-file', class: 'button button--secondary isg-order__upload', tabindex: '0', role: 'button',
      onkeydown: function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fin.click(); } } }, [label]));
    var ul = el('ul', { class: 'isg-order__filelist', role: 'list' });
    target.fileIds.forEach(function (fid) {
      var f = self.files[fid];
      if (!f) return;
      var rin = el('input', { type: 'file', id: idBase + '-r-' + fid, accept: fin.getAttribute('accept'), class: 'visually-hidden' });
      rin.addEventListener('change', function () { if (rin.files.length) self.addFiles(target, [rin.files[0]], fid); });
      ul.appendChild(el('li', { class: 'isg-order__file is-' + f.status }, [
        f.thumb ? el('img', { src: f.thumb, alt: '', class: 'isg-order__thumb' }) : el('span', { class: 'isg-order__thumb isg-order__thumb--doc', 'aria-hidden': 'true', text: f.type === 'application/pdf' ? 'PDF' : '…' }),
        el('span', { class: 'isg-order__file-text' }, [
          el('span', { class: 'isg-order__file-name', text: f.name }),
          el('span', { class: 'isg-order__file-status', text: f.status === 'checking' ? 'Checking…' : f.status === 'error' ? f.message
            : f.url ? 'Uploaded' : sizeText(f.size) + ' · ready – uploads when you add to basket' })
        ]),
        rin,
        el('label', { for: idBase + '-r-' + fid, class: 'isg-order__link', tabindex: '0', role: 'button',
          onkeydown: function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); rin.click(); } } }, ['Replace']),
        el('button', { type: 'button', class: 'isg-order__link', 'aria-label': 'Remove ' + f.name, onclick: function () { self.removeFile(target, fid); } }, ['Remove'])
      ]));
    });
    box.appendChild(ul);
    // Reuse a file already added elsewhere in this order (uploaded once, linked on every line).
    var others = Object.keys(this.files).filter(function (fid) { return target.fileIds.indexOf(fid) === -1 && self.files[fid].status === 'ready'; });
    if (others.length) {
      box.appendChild(el('p', { class: 'isg-order__reuse' }, ['Use a file you’ve already added: '].concat(others.map(function (fid) {
        return el('button', { type: 'button', class: 'isg-order__pill', onclick: function () { target.fileIds.push(fid); self.renderDesign(); self.update(); } }, ['+ ' + self.files[fid].name]);
      }))));
    }
    if (target._msg) { box.appendChild(el('p', { class: 'isg-order__error', role: 'alert', text: target._msg })); target._msg = null; }
    return box;
  };

  // Text: one text for every item, or a different text for each item (one per line, per colour and size).
  P.textBlock = function (target, dzId, idBase, label, hint) {
    var self = this, box = el('div', { class: 'isg-order__field' });
    var tid = idBase + '-text';
    if (!target.perItem) {
      var t = el('input', { type: 'text', id: tid, class: 'isg-order__input', maxlength: '200', value: target.text || '', autocomplete: 'off' });
      t.addEventListener('input', function () { target.text = t.value; self.update(); });
      box.appendChild(el('label', { for: tid, class: 'isg-order__label', text: label }));
      box.appendChild(t);
    }
    var cid = idBase + '-each';
    var cb = el('input', { type: 'checkbox', id: cid, checked: !!target.perItem });
    cb.addEventListener('change', function () { target.perItem = cb.checked; self.renderDesign(); self.update(); });
    box.appendChild(el('label', { for: cid, class: 'isg-order__check' }, [cb, ' Different text on each item (for example, names)']));
    if (target.perItem) {
      box.appendChild(el('p', { class: 'isg-order__hint', text: hint }));
      var al = C.allocation(this.lines(), this.coreDesigns(), this.same || this.designs.length < 2, this.alloc);
      this.lines().forEach(function (l) {
        var n = C.toInt(al[l.key] && al[l.key][dzId]);
        if (!n) return;
        var aid = idBase + '-each-' + l.key;
        var ta = el('textarea', { id: aid, class: 'isg-order__input', rows: String(Math.min(Math.max(n, 2), 8)), maxlength: String(80 * n + 50) });
        ta.value = (target.textEach || {})[l.key] || '';
        ta.addEventListener('input', function () { target.textEach = target.textEach || {}; target.textEach[l.key] = ta.value; self.update(); });
        box.appendChild(el('label', { for: aid, class: 'isg-order__label', text: l.label + ': ' + n + ' line' + (n === 1 ? '' : 's') }));
        box.appendChild(ta);
      });
    }
    return box;
  };

  P.notesBlock = function (target, idBase, label, placeholder) {
    var self = this, nid = idBase + '-notes';
    var ta = el('textarea', { id: nid, class: 'isg-order__input', rows: '2', maxlength: '500', placeholder: placeholder });
    ta.value = target.notes || '';
    ta.addEventListener('input', function () { target.notes = ta.value; self.update(); });
    return el('div', { class: 'isg-order__field' }, [el('label', { for: nid, class: 'isg-order__label', text: label }), ta]);
  };

  P.printCard = function (dz, p, i) {
    var self = this, d = this.data;
    var card = el('div', { class: 'isg-order__card isg-order__print' });
    card.appendChild(el('div', { class: 'isg-order__card-head' }, [
      el('h4', { class: 'isg-order__h4', text: 'Print ' + (i + 1) }),
      el('button', { type: 'button', class: 'isg-order__link', onclick: function () {
        dz.prints = dz.prints.filter(function (x) { return x !== p; }); self.dropUnusedFiles(); self.renderDesign(); self.update(); self.announce('Print removed.');
      } }, ['Remove print'])
    ]));
    var lid = 'IsgLoc-' + p.uid;
    var sel = el('select', { id: lid, class: 'isg-order__select' }, [el('option', { value: '', text: 'Choose a location' })]);
    d.locations.forEach(function (l) {
      if (!self.locSizes(l).length) return;
      sel.appendChild(el('option', { value: l.key, selected: p.location === l.key }, [l.name + (l.note ? ' (' + l.note + ')' : '')]));
    });
    sel.addEventListener('change', function () {
      p.location = sel.value;
      var loc = self.locByKey[p.location];
      var ks = loc ? self.locSizes(loc) : [];
      if (!loc || ks.indexOf(p.sizeKey) === -1) p.sizeKey = ks.length === 1 ? ks[0] : '';
      self.renderDesign(); self.update();
    });
    card.appendChild(el('div', { class: 'isg-order__field' }, [el('label', { for: lid, class: 'isg-order__label', text: 'Location' }), sel,
      self.s.sideHint ? el('span', { class: 'isg-order__hint', text: self.s.sideHint }) : null,
      el('p', { class: 'isg-order__field-error', 'data-o-err': 'loc-' + p.uid, id: lid + '-err', hidden: true })]));
    var loc = this.locByKey[p.location];
    if (loc) {
      var fs = el('fieldset', { class: 'isg-order__fieldset' }, [el('legend', { class: 'isg-order__label', text: 'Print size' })]);
      var area = self.locArea(loc);
      if (area) fs.appendChild(el('p', { class: 'isg-order__hint', text: 'Print area here: up to ' + area[0] + ' × ' + area[1] + ' cm' + (loc.areaBySize ? ' on the smallest size in your order' : '') + '.' }));
      self.locSizes(loc).forEach(function (k) {
        var s = self.sizesByKey[k], rid = 'IsgSize-' + p.uid + '-' + k;
        var r = el('input', { type: 'radio', name: 'IsgSize-' + p.uid, id: rid, value: k, checked: p.sizeKey === k });
        r.addEventListener('change', function () { p.sizeKey = k; self.update(); });
        fs.appendChild(el('label', { for: rid, class: 'isg-order__option' }, [r, el('span', {}, [
          el('strong', { text: s.label }), ' – up to ' + s.maxW + ' × ' + s.maxH + ' cm', el('span', { class: 'isg-order__price', text: ' +' + self.money(s.price) + ' per item' })
        ])]));
      });
      fs.appendChild(el('p', { class: 'isg-order__field-error', 'data-o-err': 'size-' + p.uid, hidden: true }));
      card.appendChild(fs);
    }
    card.appendChild(el('p', { class: 'isg-order__label', id: 'IsgArt-' + p.uid, text: 'What should we print?' }));
    card.appendChild(el('p', { class: 'isg-order__field-error', 'data-o-err': 'art-' + p.uid, hidden: true }));
    card.appendChild(this.fileBlock(p, 'IsgP-' + p.uid, 'Upload artwork'));
    card.appendChild(this.textBlock(p, dz.id, 'IsgP-' + p.uid, 'Or text to print (optional)', 'Type one line per item, in order. Each line is printed on one item.'));
    card.appendChild(this.notesBlock(p, 'IsgP-' + p.uid, 'Instructions for this print (optional)', 'For example: logo in navy, centred, about 10 cm wide'));
    return card;
  };

  P.includedCard = function (dz) {
    var self = this, base = 'IsgD-' + this.data.sectionId + '-' + dz.id;
    var card = el('div', { class: 'isg-order__card' });
    card.appendChild(el('p', { class: 'isg-order__label', id: base + '-art', text: this.s.allowTextOnly ? 'Your photos or artwork (or just text below)' : 'Your photos or artwork' }));
    card.appendChild(el('p', { class: 'isg-order__field-error', 'data-o-err': 'inc-art-' + dz.id, hidden: true }));
    card.appendChild(this.fileBlock(dz, base, 'Upload photos or artwork'));
    card.appendChild(this.textBlock(dz, dz.id, base, 'Name, message or other text (optional)', 'Type one line per item, in order.'));
    if (this.layouts.length) {
      var fs = el('fieldset', { class: 'isg-order__fieldset' }, [el('legend', { class: 'isg-order__label', text: 'Layout (included in the price)' })]);
      this.layouts.forEach(function (lay, i) {
        var rid = base + '-lay-' + i;
        var r = el('input', { type: 'radio', name: base + '-lay', id: rid, value: lay, checked: dz.layout === lay });
        r.addEventListener('change', function () { dz.layout = lay; self.update(); });
        fs.appendChild(el('label', { for: rid, class: 'isg-order__option' }, [r, el('span', { text: lay })]));
      });
      fs.appendChild(el('p', { class: 'isg-order__field-error', 'data-o-err': 'inc-layout-' + dz.id, hidden: true }));
      card.appendChild(fs);
    }
    card.appendChild(this.notesBlock(dz, base, 'Layout instructions (optional)', 'For example: photo on the left, names underneath'));
    return card;
  };

  // Which items get which design: a number per design for every colour and size (or option) ordered.
  P.allocTable = function (lines) {
    var self = this, box = el('div', { class: 'isg-order__alloc' });
    box.appendChild(el('h3', { class: 'isg-order__h3', text: 'Which items get which design?' }));
    box.appendChild(el('p', { class: 'isg-order__hint', text: 'Split each line between your designs. The numbers on each line must add up to the quantity ordered.' }));
    if (!lines.length) { box.appendChild(el('p', { class: 'isg-order__hint', text: 'Choose your items first.' })); return box; }
    lines.forEach(function (l) {
      self.alloc[l.key] = self.alloc[l.key] || {};
      var a = self.alloc[l.key];
      var sum = 0; self.designs.forEach(function (d) { sum += C.toInt(a[d.id]); });
      if (!sum) { a[self.designs[0].id] = l.items; }
      var row = el('div', { class: 'isg-order__alloc-row', role: 'group', 'aria-label': l.label });
      row.appendChild(el('span', { class: 'isg-order__alloc-name', text: l.label + ' (' + l.items + ')' }));
      self.designs.forEach(function (d) {
        var iid = 'IsgA-' + l.key + '-' + d.id;
        var inp = el('input', { type: 'number', id: iid, min: '0', max: String(l.items), step: '1', inputmode: 'numeric', class: 'isg-order__qty', value: String(C.toInt(a[d.id])) });
        inp.addEventListener('input', function () { a[d.id] = C.toInt(inp.value); self.update(); });
        inp.addEventListener('change', function () { self.refreshIfShapeChanged(); });
        row.appendChild(el('span', { class: 'isg-order__alloc-cell' }, [el('label', { for: iid, text: 'Design ' + d.id }), inp]));
      });
      box.appendChild(row);
    });
    return box;
  };

  /* ---------- step 2 for photo prints and frames ---------- */
  function keyClick(input) {
    return function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); } };
  }
  function cap(w) { return w.charAt(0).toUpperCase() + w.slice(1); }
  var PHOTO_ACCEPT = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';

  P.renderPhotoDesign = function (wrap) {
    var self = this, lines = this.lines(), max = this.s.maxPhotoSets || 20;
    this.syncPhotoRows(lines);
    this.dropUnusedFiles();
    if (!lines.length) { wrap.appendChild(el('p', { class: 'isg-order__hint', text: 'Choose your items first.' })); return; }
    lines.forEach(function (l) {
      var r = l.row;
      var card = el('div', { class: 'isg-order__card isg-order__design', 'data-o-photorow': String(r.uid) });
      card.appendChild(el('div', { class: 'isg-order__card-head' }, [el('h3', { class: 'isg-order__h3', text: l.label })]));
      if (!l.printed) {
        card.appendChild(el('p', { class: 'isg-order__hint', text: 'Frame only – no photo needed.' }));
        wrap.appendChild(card);
        return;
      }
      if (l.units > 1) {
        var qn = 'IsgSame-' + r.uid, plural = l.count > 1 ? 's' : '';
        var fs = el('fieldset', { class: 'isg-order__fieldset isg-order__same' }, [
          el('legend', { class: 'isg-order__label', text: 'Same photo' + plural + ' in all ' + l.units + ' ' + l.noun + 's?' })]);
        [['yes', 'Yes'], ['no', 'No, different photo' + plural + ' in each ' + l.noun]].forEach(function (o) {
          var rid = qn + '-' + o[0];
          var inp = el('input', { type: 'radio', name: qn, id: rid, value: o[0], checked: (o[0] === 'yes') === r.same,
            disabled: o[0] === 'no' && r.same && l.units > max });
          inp.addEventListener('change', function () { r.same = o[0] === 'yes'; self.renderDesign(); self.update(); });
          fs.appendChild(el('label', { for: rid, class: 'isg-order__radio' }, [inp, ' ' + o[1]]));
        });
        if (l.units > max) fs.appendChild(el('p', { class: 'isg-order__hint', text: 'Different photos can be given for up to ' + max + ' ' + l.noun + 's in one choice. For more, add them as separate choices or email us.' }));
        card.appendChild(fs);
      }
      r.sets.forEach(function (set, si) { card.appendChild(self.photoSet(l, set, si)); });
      wrap.appendChild(card);
    });
  };

  P.photoSet = function (l, set, si) {
    var self = this, r = l.row, base = 'IsgPh-' + set.uid;
    var box = el('div', { class: 'isg-order__photoset' });
    if (r.sets.length > 1) box.appendChild(el('h4', { class: 'isg-order__h4', text: cap(l.noun) + ' ' + (si + 1) + ' of ' + r.sets.length }));
    var empty = set.slots.filter(function (x) { return !x; }).length;
    if (empty) {
      var fin = el('input', { type: 'file', id: base + '-file', multiple: l.count > 1, accept: PHOTO_ACCEPT, class: 'visually-hidden' });
      fin.addEventListener('change', function () { self.addPhotos(l, set, fin.files, null); fin.value = ''; });
      box.appendChild(fin);
      box.appendChild(el('label', { for: base + '-file', class: 'button button--secondary isg-order__upload', tabindex: '0', role: 'button', onkeydown: keyClick(fin) },
        [l.count === 1 ? 'Choose your photo' : empty === l.count ? 'Choose ' + l.count + ' photos' : 'Choose photos for the ' + empty + ' empty space' + (empty === 1 ? '' : 's')]));
      if (l.count > 1) box.appendChild(el('p', { class: 'isg-order__hint', text: 'You can choose several at once: they fill the spaces in order, starting with Photo 1.' }));
    }
    var ul = el('ul', { class: 'isg-order__filelist', role: 'list' });
    set.slots.forEach(function (fid, i) {
      var f = fid ? self.files[fid] : null;
      var slotName = l.count === 1 ? 'Your photo' : 'Photo ' + (i + 1) + ' of ' + l.count;
      var rid = base + '-s' + i;
      var rin = el('input', { type: 'file', id: rid, accept: PHOTO_ACCEPT, class: 'visually-hidden' });
      rin.addEventListener('change', function () { if (rin.files.length) self.addPhotos(l, set, [rin.files[0]], i); rin.value = ''; });
      if (!f) {
        if (l.count === 1) return;   // the Choose button above covers a single photo
        ul.appendChild(el('li', { class: 'isg-order__file is-empty' }, [
          el('span', { class: 'isg-order__thumb isg-order__thumb--doc', 'aria-hidden': 'true', text: String(i + 1) }),
          el('span', { class: 'isg-order__file-text' }, [el('span', { class: 'isg-order__file-name', text: slotName }), el('span', { class: 'isg-order__file-status', text: 'Not added yet' })]),
          rin,
          el('label', { for: rid, class: 'isg-order__link', tabindex: '0', role: 'button', 'aria-label': 'Choose ' + slotName, onkeydown: keyClick(rin) }, ['Choose'])
        ]));
        return;
      }
      var q = f.status === 'ready' && f.px ? C.qualityNote(f.px, l.sizeIn, set.fit, self.s.minPpi) : null;
      var status = f.status === 'checking' ? 'Checking…' : f.status === 'error' ? f.message
        : f.url ? 'Uploaded' : sizeText(f.size) + (f.px ? ' · ' + f.px[0] + ' × ' + f.px[1] + ' px' : '') + ' · ready – uploads when you add to basket';
      ul.appendChild(el('li', { class: 'isg-order__file is-' + f.status + (q ? ' is-warn' : '') }, [
        f.thumb ? el('img', { src: f.thumb, alt: '', class: 'isg-order__thumb' }) : el('span', { class: 'isg-order__thumb isg-order__thumb--doc', 'aria-hidden': 'true', text: f.type === 'application/pdf' ? 'PDF' : '…' }),
        el('span', { class: 'isg-order__file-text' }, [
          l.count > 1 ? el('span', { class: 'isg-order__file-label', text: slotName }) : null,
          el('span', { class: 'isg-order__file-name', text: f.name }),
          el('span', { class: 'isg-order__file-status', text: status }),
          q ? el('span', { class: 'isg-order__warn', text: q.message }) : null
        ]),
        rin,
        el('label', { for: rid, class: 'isg-order__link', tabindex: '0', role: 'button', 'aria-label': 'Replace ' + slotName, onkeydown: keyClick(rin) }, ['Replace']),
        el('button', { type: 'button', class: 'isg-order__link', 'aria-label': 'Remove ' + slotName, onclick: function () { self.removePhoto(set, i, slotName); } }, ['Remove'])
      ]));
    });
    box.appendChild(ul);
    // a photo already added to this order can go in the next empty space (uploaded once, linked on each line)
    var others = Object.keys(this.files).filter(function (fid) { return set.slots.indexOf(fid) === -1 && self.files[fid].status === 'ready'; });
    if (empty && others.length) {
      box.appendChild(el('p', { class: 'isg-order__reuse' }, ['Use a photo you’ve already added: '].concat(others.map(function (fid) {
        return el('button', { type: 'button', class: 'isg-order__pill', onclick: function () {
          var k = set.slots.indexOf(null); if (k !== -1) set.slots[k] = fid; self.renderDesign(); self.update();
        } }, ['+ ' + self.files[fid].name]);
      }))));
    }
    if (set._msg) box.appendChild(el('p', { class: 'isg-order__error', role: 'alert', text: set._msg }));   // stays until the next change to this set
    var space = this.persIndex >= 0 ? 'space in the frame' : 'print';
    var ff = el('fieldset', { class: 'isg-order__fieldset' }, [el('legend', { class: 'isg-order__label', text: l.count > 1 ? 'How should the photos fit?' : 'How should the photo fit?' })]);
    [['fill', 'Fill the ' + space, 'We trim the edges if your photo is a different shape.'],
     ['whole', 'Show the whole photo', 'Nothing is trimmed; a white border may show where the shapes differ.']].forEach(function (o) {
      var rid = base + '-fit-' + o[0];
      var inp = el('input', { type: 'radio', name: base + '-fit', id: rid, value: o[0], checked: set.fit === o[0] });
      inp.addEventListener('change', function () { set.fit = o[0]; self.renderDesign(); self.update(); });
      ff.appendChild(el('label', { for: rid, class: 'isg-order__option' }, [inp, el('span', {}, [el('strong', { text: o[1] }), el('span', { class: 'isg-order__option-sub', text: o[2] })])]));
    });
    box.appendChild(ff);
    box.appendChild(this.notesBlock(set, base, l.count > 1 ? 'Which photo goes where? (optional)' : 'Instructions (optional)',
      l.count > 1 ? 'For example: Photo 1 in the large space in the middle' : 'For example: please keep everyone in the group in the picture'));
    return box;
  };

  // Put chosen photos into a set: into one space (replace), or into the empty spaces in order.
  P.addPhotos = function (l, set, list, slotIndex) {
    var self = this, arr = Array.prototype.slice.call(list || []), targets = [];
    set._msg = null;
    if (slotIndex != null) { targets = [slotIndex]; arr = arr.slice(0, 1); }
    else set.slots.forEach(function (x, i) { if (!x) targets.push(i); });
    if (arr.length > targets.length) {
      var extra = arr.length - targets.length;
      set._msg = 'This ' + l.noun + ' takes ' + l.count + ' photo' + (l.count === 1 ? '' : 's') + ', so ' + extra + ' of the photos you chose ' + (extra === 1 ? 'wasn’t' : 'weren’t') + ' added.';
      arr = arr.slice(0, targets.length);
    }
    arr.forEach(function (file, k) { set.slots[targets[k]] = self.newFile(file).id; });
    this.dropUnusedFiles();
    this.renderDesign();
    this.update();
  };
  P.removePhoto = function (set, i, slotName) {
    set.slots[i] = null;
    set._msg = null;
    this.dropUnusedFiles();
    this.renderDesign();
    this.update();
    this.announce(slotName + ' removed.');
  };

  /* ---------- step 3: review ---------- */
  P.update = function () {
    var self = this, st = this.state();
    var al = this.flow === 'photo' ? {} : C.allocation(st.lines, st.designs, st.same, st.alloc);
    var t = C.totals(this.flow, st.lines, st.designs, al, this.sizesByKey || {}, { tiers: this.tiers, countsPrints: this.s.bulkCountsPrints });
    // per-card totals
    if (this.flow === 'clothing') {
      this.rows.forEach(function (r) {
        var n = 0, cost = 0;
        st.lines.forEach(function (l) { if (l.colour === r.colour) { n += l.items; cost += l.items * l.price; } });
        var node = self.box.querySelector('[data-o-rowtotal="' + (root.CSS && CSS.escape ? CSS.escape(r.colour) : r.colour) + '"]');
        if (node) node.textContent = n ? n + ' × ' + (r.colour || 'items') + ': ' + self.money(cost) + ' before printing' : '';
      });
    } else {
      st.lines.forEach(function (l) {
        var node = self.box.querySelector('[data-o-incrow="' + l.rowUid + '"]');
        if (node && self.flow === 'photo') {
          node.textContent = (l.noun === 'pack' ? l.units + ' pack' + (l.units === 1 ? '' : 's') + ' (' + l.items + ' prints)' : l.units + ' ' + l.noun + (l.units === 1 ? '' : 's')) +
            ': ' + self.money(l.price * l.units) + (l.printed ? ', photo printing included' : ', frame only');
        } else if (node) node.textContent = l.items + ' item' + (l.items === 1 ? '' : 's') + ': ' + self.money(l.price * l.units) + ', personalisation included';
      });
    }
    // summary
    var sum = this.q('[data-o-summary]');
    sum.innerHTML = '';
    if (!st.lines.length) sum.appendChild(el('p', { class: 'isg-order__hint', text: 'Your order summary will appear here.' }));
    else {
      var dl = el('dl', { class: 'isg-order__rows' });
      function row(k, v, cls) { dl.appendChild(el('dt', { class: cls, text: k })); dl.appendChild(el('dd', { class: cls, text: v })); }
      st.lines.forEach(function (l) { row(l.label + (self.flow === 'clothing' ? ' × ' + l.items : ''), self.money(l.price * l.units)); });
      row(this.flow === 'clothing' ? 'Garments (' + t.items + ')' : 'Products (' + t.items + ' item' + (t.items === 1 ? '' : 's') + ')', this.money(t.goods), 'is-sub');
      if (this.flow === 'clothing' && st.plain) {
        row('Printing', 'None – plain items', 'is-sub');
      } else if (this.flow === 'clothing') {
        var per = C.itemsPerDesign(st.lines, st.designs, al);
        st.designs.forEach(function (d) {
          if (!st.same && st.designs.length > 1 && !per[d.id]) return;
          d.prints.forEach(function (p, i) {
            var s = self.sizesByKey[p.sizeKey];
            if (!s) return;
            row((st.same || st.designs.length < 2 ? 'Print ' : 'Design ' + d.id + ', print ') + (i + 1) + ': ' + (p.locationLabel || 'location') + ', ' + s.label + ' – ' +
              self.money(s.price) + ' × ' + per[d.id], self.money(s.price * per[d.id]));
            var what = self.artText(p);
            if (what) row(what, '', 'is-note');
          });
        });
        row('Printing', this.money(t.prints), 'is-sub');
      } else if (this.flow === 'photo') {
        if (st.lines.some(function (l) { return l.printed; })) row('Photo printing', 'Included', 'is-sub');
      } else {
        row('Personalisation', 'Included', 'is-sub');
        st.designs.forEach(function (d) {
          var what = self.artText(d);
          if (what) row((st.designs.length > 1 && !st.same ? 'Design ' + d.id + ': ' : '') + what, '', 'is-note');
        });
      }
      if (t.discount) row('Bulk discount ' + t.bulk.current.pct + '% (' + t.bulkCount + ' items)', '−' + this.money(t.discount), 'is-discount');
      row('Total', this.money(t.total), 'is-total');
      sum.appendChild(dl);
      var notes = [];
      if (this.data.bulkEligible && this.tiers.length) {
        notes.push('Bulk discounts: ' + this.tiers.map(function (x) { return x.min + '+ items ' + x.pct + '% off'; }).join(', ') + (this.s.bulkCountsPrints ? ' (garments and prints both count).' : '.') +
          ' This is an estimate for this order on its own – Shopify works out the final discount in your basket, including other eligible items.');
        if (t.bulk.next) notes.push('Add ' + t.bulk.need + ' more item' + (t.bulk.need === 1 ? '' : 's') + ' for ' + t.bulk.next.pct + '% off.');
      } else notes.push('Any discount code can be entered at checkout.');
      notes.push('Shipping is calculated at checkout.');
      notes.forEach(function (n) { sum.appendChild(el('p', { class: 'isg-order__hint', text: n })); });
      // edit links: back to a step, keeping everything else
      var step1 = this.q('[aria-labelledby^="IsgStep1-"]'), step2 = this.q('[aria-labelledby^="IsgStep2-"]');
      sum.appendChild(el('p', { class: 'isg-order__edits' }, [
        el('button', { type: 'button', class: 'isg-order__link', onclick: function () { jumpTo(step1, self.box.querySelector('[data-o-items] .isg-order__swatch, [data-o-items] input, [data-o-items] select')); } },
          [this.flow === 'clothing' ? 'Change colours or sizes' : 'Change your choice']),
        el('button', { type: 'button', class: 'isg-order__link', onclick: function () { self.goToDesign(); } },
          [this.flow === 'clothing' ? (st.plain ? 'Add a print instead' : 'Change prints') : this.flow === 'photo' ? 'Change photos' : 'Change your design'])
      ]));
    }
    // what's stopping the order
    var probs = C.problems(st);
    var dup = {};
    st.lines.forEach(function (l) { if (dup[l.key]) probs.push(l.label + ' is chosen twice. Remove one and change the quantity instead.'); dup[l.key] = true; });
    if (this.flow === 'clothing' && !(this.data.printSizes || []).some(function (s) { return s.variantId; })) probs.unshift('Print prices aren’t set up yet, so this item can’t be ordered online. Please contact us.');
    var ul = this.q('[data-o-blockers]');
    ul.innerHTML = '';
    if (probs.length && st.lines.length) ul.appendChild(el('li', { class: 'isg-order__blockers-head', text: 'Still to do before adding to your basket:' }));
    if (st.lines.length || this.showErrors) probs.forEach(function (p) {
      var tgt = self.targetFor(p);
      ul.appendChild(el('li', {}, [tgt ? el('button', { type: 'button', class: 'isg-order__link isg-order__fix', onclick: function () { self.showErrors = true; self.update(); var t2 = self.targetFor(p); jumpTo(t2 && t2.box, t2 && t2.focus); } }, [p]) : p]));
    });
    this.fieldErrors(st);
    var btn = this.q('[data-o-submit]');
    btn.disabled = !!this.busy;
    // never aria-disabled: pressing it while something is missing explains what and jumps there
    btn.classList.toggle('is-incomplete', !!probs.length);
    if (probs.length && st.lines.length) { ul.id = ul.id || 'IsgTodo-' + this.data.sectionId; btn.setAttribute('aria-describedby', ul.id); } else btn.removeAttribute('aria-describedby');
    btn.textContent = this.busy ? 'Adding to basket…' : t.items ? 'Add ' + t.items + ' item' + (t.items === 1 ? '' : 's') + ' to basket – ' + this.money(t.total) : 'Add to basket';
    var cont = this.q('[data-o-continue]');
    if (cont) cont.hidden = !st.lines.some(function (l) { return l.items > 0; });
    this.lastProblems = probs;
    this.lastTotals = t;
    this.updateSticky();
    return t;
  };

  // What will be printed, in words, for the summary: file names and/or the text.
  P.artText = function (p) {
    var self = this, bits = [];
    var names = (p.fileIds || []).map(function (fid) { return self.files[fid] ? self.files[fid].name : null; }).filter(Boolean);
    if (names.length) bits.push((this.flow === 'clothing' ? 'Artwork: ' : 'Photos/artwork: ') + names.join(', '));
    if (p.perItem) bits.push('Different text on each item');
    else if (String(p.text || '').trim()) bits.push('Text: “' + String(p.text).trim() + '”');
    if (p.layout) bits.push('Layout: ' + p.layout);
    return bits.join(' · ');
  };

  // Where a problem can be fixed: { box, focus } in the form, or null.
  P.targetFor = function (msg) {
    var box = this.box, m;
    var step1 = box.querySelector('[aria-labelledby^="IsgStep1-"]'), step2 = box.querySelector('[aria-labelledby^="IsgStep2-"]');
    if (/^Choose a colour|^Choose what you|^Only \d+ of |is chosen twice/.test(msg)) {
      var firstQty = box.querySelector('[data-o-items] input:not([disabled]), [data-o-items] select, [data-o-items] .isg-order__swatch');
      return { box: step1, focus: firstQty };
    }
    if (this.flow === 'clothing' && (m = /^(?:Design ([A-Z]), )?print (\d+): (.*)$/.exec(msg))) {
      var di = m[1] ? this.designs.filter(function (d) { return d.id === m[1]; })[0] : this.designs[0];
      var pr = di && di.prints[C.toInt(m[2]) - 1];
      if (pr) {
        var card = box.querySelector('#IsgLoc-' + pr.uid);
        card = card ? card.closest('.isg-order__print') : null;
        if (/choose where it goes/.test(m[3])) return { box: card, focus: box.querySelector('#IsgLoc-' + pr.uid) };
        if (/print size/.test(m[3])) return { box: card, focus: box.querySelector('input[name="IsgSize-' + pr.uid + '"]') };
        return { box: box.querySelector('#IsgArt-' + pr.uid) || card, focus: box.querySelector('label[for="IsgP-' + pr.uid + '-file"]') };
      }
    }
    if (/^(?:Design [A-Z]: )?add at least one print location/.test(msg)) return { box: step2, focus: box.querySelector('[data-o-design] .isg-order__add') };
    if (/still need a design|too many assigned|isn’t used on any item/.test(msg)) return { box: box.querySelector('.isg-order__alloc') || step2, focus: box.querySelector('.isg-order__alloc input') };
    if (/^(Your design|Design [A-Z])/.test(msg) && this.flow === 'included') {
      var dm = /^Design ([A-Z])/.exec(msg), dz = dm ? dm[1] : this.designs[0].id;
      var base = 'IsgD-' + this.data.sectionId + '-' + dz;
      if (/layout/.test(msg)) return { box: box.querySelector('input[name="' + base + '-lay"]') ? box.querySelector('input[name="' + base + '-lay"]').closest('fieldset') : step2, focus: box.querySelector('input[name="' + base + '-lay"]') };
      return { box: box.querySelector('#' + base + '-art') || step2, focus: box.querySelector('label[for="' + base + '-file"]') };
    }
    return step2 ? { box: step2, focus: null } : null;
  };

  // Messages beside the fields, once the customer has tried to go ahead (Add to basket, or a "still to do" link).
  P.fieldErrors = function (st) {
    var self = this, errs = {};
    if (this.showErrors) {
      if (!st.lines.some(function (l) { return l.items > 0; })) errs.items = this.flow === 'clothing' ? (this.noColour ? 'Enter how many you need.' : 'Choose a colour, then enter how many you need in each size.') : 'Choose how many you need.';
      if (this.flow === 'clothing' && !st.plain) {
        this.designs.forEach(function (d) {
          d.prints.forEach(function (p) {
            if (!p.location) errs['loc-' + p.uid] = 'Choose where this print goes.';
            else if (!p.sizeKey) errs['size-' + p.uid] = 'Choose a print size.';
            var hasText = p.perItem || !!String(p.text || '').trim();
            if (!(p.fileIds && p.fileIds.length) && !hasText) errs['art-' + p.uid] = 'Upload your artwork, or type the text to print.';
          });
        });
      } else if (this.flow === 'included') {
        this.designs.forEach(function (d) {
          var hasText = d.perItem || !!String(d.text || '').trim();
          if (!(d.fileIds && d.fileIds.length) && !(hasText && self.s.allowTextOnly)) errs['inc-art-' + d.id] = self.s.allowTextOnly ? 'Add a photo or artwork, or type your text below.' : 'Add a photo or artwork.';
          if (self.layouts.length && !d.layout) errs['inc-layout-' + d.id] = 'Choose a layout.';
        });
      }
    }
    Array.prototype.forEach.call(this.box.querySelectorAll('[data-o-err]'), function (n) {
      var k = n.getAttribute('data-o-err'), msg = errs[k] || '';
      if (n.textContent !== msg) n.textContent = msg;
      n.hidden = !msg;
      var field = k.indexOf('loc-') === 0 ? self.box.querySelector('#IsgLoc-' + k.slice(4)) : null;
      if (field) { if (msg) { field.setAttribute('aria-invalid', 'true'); field.setAttribute('aria-describedby', field.id + '-err'); } else { field.removeAttribute('aria-invalid'); field.removeAttribute('aria-describedby'); } }
    });
  };

  /* ---------- phones: running total and next step ---------- */
  P.setupSticky = function () {
    var self = this, bar = this.q('[data-o-sticky]');
    if (!bar) return;
    this.typing = false;
    this.reviewVisible = false;
    this.formVisible = true;
    var btn = this.q('[data-o-sticky-btn]');
    btn.addEventListener('click', function () {
      var t = self.lastTotals, probs = self.lastProblems || [];
      var itemsDone = !probs.some(function (p) { return /^Choose a colour|^Choose what you|^Only \d+ of |is chosen twice/.test(p); });
      if (!itemsDone) { self.showErrors = true; self.update(); var t1 = self.targetFor(probs[0]); jumpTo(t1 && t1.box, t1 && t1.focus); return; }
      if (self.stickyAction === 'design') { self.goToDesign(); return; }
      jumpTo(self.q('[aria-labelledby^="IsgStep3-"]'), self.q('[data-o-submit]'));
    });
    // hide while typing (the on-screen keyboard needs the room) and while the review step is on screen
    this.box.addEventListener('focusin', function (e) { if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) && e.target.type !== 'radio' && e.target.type !== 'checkbox' && e.target.type !== 'file') { self.typing = true; self.updateSticky(); } });
    this.box.addEventListener('focusout', function () { self.typing = false; setTimeout(function () { self.updateSticky(); }, 50); });
    if (root.IntersectionObserver) {
      var review = this.q('[aria-labelledby^="IsgStep3-"]');
      new root.IntersectionObserver(function (es) { es.forEach(function (e) { self.reviewVisible = e.isIntersecting; }); self.updateSticky(); }, { threshold: 0.15 }).observe(review);
      new root.IntersectionObserver(function (es) { es.forEach(function (e) { self.formVisible = e.isIntersecting; }); self.updateSticky(); }, { threshold: 0 }).observe(this.box);
    }
    this.updateSticky();
  };
  P.updateSticky = function () {
    var bar = this.q('[data-o-sticky]');
    if (!bar || this.typing === undefined) return;
    var t = this.lastTotals, probs = this.lastProblems || [];
    var phone = !root.matchMedia || root.matchMedia('(max-width: 749px)').matches;
    var show = phone && t && t.items > 0 && !this.reviewVisible && this.formVisible && !this.typing && !this.busy && !this.done;
    if (show) {
      var designLeft = probs.some(function (p) { return !/^Choose a colour|^Choose what you|^Only \d+ of |is chosen twice/.test(p); });
      this.stickyAction = designLeft ? 'design' : 'review';
      this.q('[data-o-sticky-text]').textContent = t.items + ' item' + (t.items === 1 ? '' : 's') + ' · ' + this.money(t.total);
      var label = designLeft ? (this.flow === 'clothing' ? 'Continue to print' : this.flow === 'photo' ? 'Add your photos' : 'Add your design') : 'Review and add to basket';
      var b = this.q('[data-o-sticky-btn]');
      if (b.textContent !== label) b.textContent = label;
    }
    if (bar.hidden === !show) return;
    bar.hidden = !show;
    // keep the end of the form and anything scrolled into view clear of the bar
    this.box.classList.toggle('has-sticky', show);
    try { doc.documentElement.style.scrollPaddingBottom = show ? (bar.offsetHeight + 12) + 'px' : ''; } catch (e) { /* ignore */ }
  };

  /* ---------- adding to the basket ---------- */
  function readPending() { try { return JSON.parse(root.sessionStorage.getItem(PENDING_KEY) || '[]').filter(function (x) { return typeof x === 'string'; }); } catch (e) { return []; } }
  function writePending(list) { try { root.sessionStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(-10))); } catch (e) { /* memory only */ } }

  P.call = function (url, body) {
    return fetch(url, body === undefined ? { headers: { Accept: 'application/json', 'X-ISG-Sync': '1' } }
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-ISG-Sync': '1' }, body: JSON.stringify(body) }
    ).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (j) {
        if (!r.ok) throw { status: r.status, json: j };
        return j;
      });
    });
  };

  // Multipart add (a line carrying files), with upload progress. Resolves with Shopify's line item.
  P.postForm = function (fd, onProgress) {
    var url = this.data.cartAddUrl;
    return new Promise(function (resolve, reject) {
      var x = new XMLHttpRequest();
      x.open('POST', url);
      x.setRequestHeader('Accept', 'application/json');
      x.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      if (x.upload && onProgress) x.upload.onprogress = function (e) { if (e.lengthComputable) onProgress(e.loaded, e.total); };
      x.onload = function () {
        var j = null;
        try { j = JSON.parse(x.responseText); } catch (e) { /* not JSON */ }
        if (x.status >= 200 && x.status < 300 && j) resolve(j); else reject({ status: x.status, json: j });
      };
      x.onerror = function () { reject({ status: 0 }); };
      x.ontimeout = function () { reject({ status: 0, timeout: true }); };
      x.timeout = 600000;
      x.send(fd);
    });
  };

  // Remove every basket line of an attempt, then re-read the basket. Resolves { clean, left }.
  P.cleanRef = function (base) {
    var self = this;
    return this.call(this.data.cartJsUrl).then(function (cart) {
      var mine = C.linesOfRef(cart, base);
      if (!mine.length) return { clean: true, left: 0 };
      var updates = {};
      mine.forEach(function (it) { updates[it.key] = 0; });
      return self.call(self.data.cartUpdateUrl, { updates: updates }).then(function () { return self.call(self.data.cartJsUrl); }).then(function (c2) {
        var left = C.linesOfRef(c2, base).length;
        return { clean: !left, left: left };
      });
    }).catch(function () { return { clean: false, left: null }; });
  };

  P.progress = function (frac, text) {
    var w = this.q('[data-o-progress]');
    w.hidden = frac == null;
    if (frac == null) return;
    this.q('[data-o-bar]').style.width = Math.round(frac * 100) + '%';
    this.q('[data-o-progress-text]').textContent = text;
  };
  P.error = function (msg, link) {
    var e = this.q('[data-o-error]');
    e.innerHTML = '';
    if (msg) {
      e.appendChild(doc.createTextNode(msg + ' '));
      if (link) e.appendChild(el('a', { href: this.data.cartUrl, text: 'View your basket' }));
    }
    e.hidden = !msg;
  };

  P.submit = function () {
    var self = this;
    if (this.busy || this.done) return;
    this.update();
    if (this.lastProblems && this.lastProblems.length) {
      this.showErrors = true;
      this.update();
      var first = this.targetFor(this.lastProblems[0]);
      this.announce('Your order isn’t complete yet. ' + this.lastProblems[0]);
      if (first) jumpTo(first.box, first.focus);
      return;
    }
    this.busy = true;
    this.error('');
    this.update();
    var st = this.state();
    var ref = C.makeRef(function () { return root.crypto && root.crypto.getRandomValues ? root.crypto.getRandomValues(new Uint8Array(1))[0] : Math.floor(Math.random() * 256); });
    var plan = C.basketPlan(st, ref);
    if (st.plain) plan.lines.forEach(function (l) { l.properties.Printing = 'None – plain item'; });
    var pending = readPending();

    // bytes to upload, for the progress bar
    var seen = {}, totalBytes = 0;
    plan.lines.forEach(function (l) { l.files.forEach(function (f) { if (!seen[f.id] && !self.files[f.id].url) { seen[f.id] = true; totalBytes += self.files[f.id].size; } }); });
    var doneBytes = 0;
    var nFiles = Object.keys(seen).length, fileNo = 0;

    // 1. clear what earlier unfinished attempts may have left
    var chain = Promise.resolve();
    pending.forEach(function (base) {
      chain = chain.then(function () {
        return self.cleanRef(base).then(function (r) {
          if (!r.clean) throw { stage: 'leftover' };
          writePending(readPending().filter(function (x) { return x !== base; }));
        });
      });
    });
    chain = chain.then(function () {
      writePending(readPending().concat([ref]));
      self.progress(0.02, nFiles ? 'Uploading your files…' : 'Adding to your basket…');
    });
    // 2. the item lines, uploading each file once
    plan.lines.forEach(function (pl, li) {
      chain = chain.then(function () {
        var fresh = pl.files.filter(function (f) { return !self.files[f.id].url; });
        if (!fresh.length) {
          var props = Object.assign({}, pl.properties);
          pl.files.forEach(function (f) { props[f.prop] = self.files[f.id].url; });
          return self.call(self.data.cartAddUrl, { items: [{ id: pl.id, quantity: pl.quantity, properties: props }] });
        }
        var fd = new FormData();
        fd.append('id', String(pl.id));
        fd.append('quantity', String(pl.quantity));
        Object.keys(pl.properties).forEach(function (k) {
          var f = pl.files.filter(function (x) { return x.prop === k; })[0];
          if (!f) fd.append('properties[' + k + ']', pl.properties[k]);
          else if (self.files[f.id].url) fd.append('properties[' + k + ']', self.files[f.id].url);
          else fd.append('properties[' + k + ']', self.files[f.id].file, self.files[f.id].name);
        });
        var batch = fresh.reduce(function (a, f) { return a + self.files[f.id].size; }, 0);
        fileNo += fresh.length;
        return self.postForm(fd, function (loaded, total) {
          var part = total ? loaded / total : 0;
          self.progress(0.02 + 0.83 * (totalBytes ? (doneBytes + part * batch) / totalBytes : 1),
            'Uploading file' + (nFiles > 1 ? 's ' + Math.min(fileNo, nFiles) + ' of ' + nFiles : '') + ' – ' + Math.round((totalBytes ? (doneBytes + part * batch) / totalBytes : 1) * 100) + '%');
        }).then(function (item) {
          doneBytes += batch;
          var line = item && item.items ? item.items[0] : item;
          fresh.forEach(function (f) {
            var v = line && line.properties && line.properties[f.prop];
            if (!C.isStoredLink(v)) throw { stage: 'upload', file: self.files[f.id].name };
            self.files[f.id].url = v;
          });
        });
      });
    });
    // 3. print charges, then 4. check the whole basket against the plan
    chain = chain.then(function () {
      self.progress(0.9, plan.charges.length ? 'Adding print charges and checking your basket…' : 'Checking your basket…');
      if (!plan.charges.length) return null;
      return self.call(self.data.cartAddUrl, { items: plan.charges });
    }).then(function () { return self.call(self.data.cartJsUrl); }).then(function (cart) {
      var wrong = C.verifyAttempt(cart, plan, ref);
      if (wrong.length) throw { stage: 'verify', wrong: wrong };
      writePending(readPending().filter(function (x) { return x !== ref; }));
      self.progress(1, 'Added. Taking you to your basket…');
      self.busy = false;   // so the "leave this page?" guard doesn't stop the move to the basket
      self.done = true;
      root.location.href = self.data.cartUrl;
    });

    chain.catch(function (err) {
      if (root.console) root.console.warn('ISG order form: add to basket failed', err);
      if (err && err.stage === 'leftover') {
        self.finishFail('Items from an earlier attempt may still be in your basket and we couldn’t remove them, so we haven’t added anything new. Please check your basket before trying again.', true);
        return;
      }
      var why = err && err.stage === 'upload' ? 'Your file ' + (err.file || '') + ' didn’t finish uploading.'
        : err && err.status === 422 ? ((err.json && (err.json.description || err.json.message)) || 'Shopify couldn’t add one of the items.')
        : err && err.stage === 'verify' ? 'Your basket didn’t match your order once everything was added.'
        : 'The connection dropped while adding your order.';
      self.progress(0.95, 'Removing what was added…');
      self.cleanRef(ref).then(function (r) {
        if (r.clean) {
          writePending(readPending().filter(function (x) { return x !== ref; }));
          self.finishFail(why + ' We’ve checked your basket and nothing from this attempt is in it. Please try again.', false);
        } else {
          self.finishFail(why + ' Some items from this attempt may still be in your basket. We’ll remove them before you try again, or you can remove them yourself.', true);
        }
      });
    });
  };

  P.finishFail = function (msg, link) {
    this.busy = false;
    this.progress(null);
    // files uploaded during a failed attempt are linked from removed lines; upload them again next time
    var self = this;
    Object.keys(this.files).forEach(function (fid) { self.files[fid].url = null; });
    this.error(msg, link);
    this.renderDesign();
    this.update();
  };

  function boot() {
    setOffsetVar();
    root.addEventListener('resize', setOffsetVar);
    Array.prototype.forEach.call(doc.querySelectorAll('[data-isg-order]'), function (box) {
      if (box.__isgOrder || !doc.getElementById('IsgOrderData-' + box.getAttribute('data-isg-order'))) return;
      var f = box.__isgOrder = new Form(box);
      box.querySelector('[data-o-submit]').addEventListener('click', function () { f.submit(); });
      root.addEventListener('beforeunload', function (e) { if (f.busy) { e.preventDefault(); e.returnValue = ''; } });
      root.addEventListener('resize', function () { f.updateSticky(); });
      // old "Start your order" links (#isg-order) land on the colours, clear of the sticky header
      if (root.location.hash === '#isg-order') setTimeout(function () { jumpTo(box, box.querySelector('.isg-order__swatch, [data-o-items] input, [data-o-items] select')); }, 60);
    });
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
  root.ISGOrderForm = Form;
})(window);
